import { useState } from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import EditableTagCell from "./EditableTagCell"
import UnavailableChipLabel from "ui/Blackout/UnavailableChipLabel"
import {
  getAssignedCrew,
  getAvailableStaff,
  assignMultipleStaff,
  unassignStaffMember,
  selectAssignedCrew,
  selectUnassignOperationLoading
} from "store/staffAssignments"
import styles from "./BookingTable.module.css"

// A member a blackout has made unavailable says so on the chip; everyone else is a plain string,
// which is what the overflow "+N more" counting and the picker both already expect.
const renderCrewLabel = (member) => {
  const roleSuffix = member.role ? ` (${member.role})` : ""
  const label = `${member.name}${roleSuffix}`
  if (!member.isUnavailable) return label
  return <UnavailableChipLabel label={label} reason={member.unavailableReason} date={member.unavailableDate} />
}

// filter=all returns every crew member across availability buckets; flatten them into one
// list, tagging each with the bucket it came from so the picker can surface a
// Day Off / Booking Conflict badge (see toCrewItem). The tag gets its own key: every member
// already carries `status` — their account status, "active" — which would mask the bucket.
const withBucket = (list, bucket) => (list || []).map((member) => ({ ...member, bucket }))

export const normalizeStaffList = (data) => {
  if (Array.isArray(data)) return data
  if (data && typeof data === "object") {
    return [...withBucket(data.available, "available"), ...withBucket(data.unavailable, "unavailable"), ...withBucket(data.has_bookings, "has_bookings")]
  }
  return []
}

// The staff member's org role ("Admin", "Crew Member", …) as the availability listing reports it.
// This is the label both crew panels show — NOT the assignment's `assignment_role`, which the
// frontend always posts as "crew_member" and so carries no information.
export const staffRoleLabel = (staff) => {
  const primaryRole = staff.role || staff.primary_role || staff.roles?.[0]
  return typeof primaryRole === "object" ? primaryRole?.name || "" : primaryRole || ""
}

const toCrewItem = (staff) => ({
  id: staff.id,
  name: staff.full_name || `${staff.first_name || ""} ${staff.last_name || ""}`.trim(),
  role: staffRoleLabel(staff),
  bucket: staff.bucket
})

// A crew member who can't take this booking — an overlapping booking (is_booking_conflict) or a
// day off (the "unavailable" bucket) — is badged and can't be picked. In toggle mode a member who
// is already on the booking stays toggleable regardless, so an unavailable one can still be taken
// off (see EditableTagCell). The conflict flag is read per member rather than from the bucket.
const decorateCrewItem = (staff, t) => {
  const item = toCrewItem(staff)
  if (staff.is_booking_conflict) {
    return { ...item, badge: { text: t("booking.crew.status_booking_conflict", { defaultValue: "Booking Conflict" }), color: "orange" }, disabled: true }
  }
  if (item.bucket === "unavailable") {
    return { ...item, badge: { text: t("booking.crew.status_day_off", { defaultValue: "Day Off" }), color: "red" }, disabled: true }
  }
  return { ...item, badge: null, disabled: false }
}

const CrewCell = ({ initialCrew, bookingId, emptyLabel, alwaysEditable, isToggleMode, onCountChange, onAssignmentsChange }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const assignedCrew = useSelector(selectAssignedCrew)
  const removeLoading = useSelector(selectUnassignOperationLoading)
  const [groups, setGroups] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  const handleEditStart = () => {
    if (bookingId) dispatch(getAssignedCrew(bookingId))
  }

  const handlePickerOpen = () => {
    if (!bookingId) return
    // filter=all returns three availability buckets; the bucket only drives each member's
    // badge/disabled state (see decorateCrewItem). Grouping stays keyed on is_assigned —
    // assigned crew under "Available", the rest under "Other Crew" — matching the old booking
    // detail, where a member with a booking conflict is listed but disabled from re-assigning.
    setIsLoading(true)
    dispatch(getAvailableStaff(bookingId, { filter: "all" }))
      .then((res) => {
        const staff = normalizeStaffList(res?.data)
        const decorate = (member) => decorateCrewItem(member, t)
        setGroups([
          { key: "available", label: "Available", items: staff.filter((member) => member.is_assigned).map(decorate) },
          { key: "others", label: "Other Crew", items: staff.filter((member) => !member.is_assigned).map(decorate) }
        ])
      })
      .catch(() => {})
      .finally(() => setIsLoading(false))
  }

  // Returns nothing, so EditableTagCell adds the chips optimistically — unchanged. The
  // notification is chained on for hosts that need to refetch once the assign lands.
  const handleAdd = (items) => {
    if (!bookingId) return
    dispatch(assignMultipleStaff(bookingId, items.map((item) => ({ profile_id: item.id, role: "crew_member" }))))
      .then(() => onAssignmentsChange?.())
      .catch(() => {})
  }

  const handleRemove = (item) => {
    // Detail chips carry assignmentId directly; picker-added chips fall back to the
    // fetched assigned list, matching by profile id.
    const assignmentId =
      item.assignmentId ?? assignedCrew.find((a) => a.profileId === item.profileId || a.profileId === item.id)?.id
    if (assignmentId && bookingId) {
      // Resolve with the unassign result so EditableTagCell still drops the chip on success,
      // and notify the host (e.g. the equipment calendar) so it can refetch what it shows.
      return dispatch(unassignStaffMember(assignmentId, bookingId)).then((res) => {
        onAssignmentsChange?.()
        return res
      })
    }
    return undefined
  }

  return (
    <EditableTagCell
      initialItems={initialCrew}
      groups={groups}
      renderLabel={renderCrewLabel}
      addLabel="Add Crew"
      updateLabel={t("booking.crew.update_crew", { defaultValue: "Update Crew" })}
      chipClassName={styles.crewLabel}
      onEditStart={handleEditStart}
      onPickerOpen={handlePickerOpen}
      onAdd={handleAdd}
      onRemove={handleRemove}
      loading={isLoading}
      removeLoading={removeLoading}
      emptyLabel={emptyLabel}
      alwaysEditable={alwaysEditable}
      isToggleMode={isToggleMode}
      onCountChange={onCountChange}
    />
  )
}

CrewCell.propTypes = {
  initialCrew: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string, role: PropTypes.string })),
  bookingId: PropTypes.string,
  emptyLabel: PropTypes.string,
  alwaysEditable: PropTypes.bool,
  // The picker opens with the booking's crew ticked and unticking one unassigns it. Only for hosts
  // whose chip ids are profile ids, which is what the picker's items are keyed on.
  isToggleMode: PropTypes.bool,
  onCountChange: PropTypes.func,
  // Fired after an assign/unassign succeeds, for hosts rendering data that the change invalidates.
  onAssignmentsChange: PropTypes.func
}

CrewCell.defaultProps = {
  initialCrew: [],
  emptyLabel: "",
  alwaysEditable: false,
  isToggleMode: false
}

export default CrewCell
